import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { CardInformado } from "@/modules/visitantes/components/CardInformado";
import { CampoTexto } from "@/modules/visitantes/components/Campos";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import {
  useDescartarInformado,
  useIndicacoes,
  useRegistrarVisitaDoInformado,
} from "@/modules/visitantes/hooks/useVisitantes";
import type { Indicacao, VisitanteLista } from "@/modules/visitantes/services/visitantesService";
import { normalizarNome } from "@/modules/visitantes/utils/regras";

const BOTAO =
  "inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-[13px] font-semibold text-text transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50";

interface Props {
  /** Só o ministério responsável avalia; o administrador apenas lê. */
  podeAvaliar: boolean;
  visitantes: VisitanteLista[];
  onAviso: (texto: string) => void;
}

/** "Visitantes pendentes": o que líderes de outros ministérios cadastraram para o ministério responsável avaliar (Adicionar / Não adicionar). */
export function AbaInformados({ podeAvaliar, visitantes, onAviso }: Props) {
  const navigate = useNavigate();
  const { data = [], isLoading, isError, refetch } = useIndicacoes(true);
  const descartar = useDescartarInformado();
  const soVisita = useRegistrarVisitaDoInformado();

  const [ver, setVer] = useState<"pendentes" | "avaliadas">("pendentes");
  const [paraDescartar, setParaDescartar] = useState<Indicacao | null>(null);
  const [paraSoVisita, setParaSoVisita] = useState<{
    i: Indicacao;
    existente: VisitanteLista;
  } | null>(null);
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const pendentes = data.filter((i) => i.situacao === "pendente");
  const avaliadas = data.filter((i) => i.situacao !== "pendente");
  const lista = ver === "pendentes" ? pendentes : avaliadas;

  const porNome = useMemo(() => {
    const mapa = new Map<string, VisitanteLista>();
    for (const v of visitantes) mapa.set(normalizarNome(v.nome), v);
    return mapa;
  }, [visitantes]);

  async function confirmarDescarte() {
    if (!paraDescartar) return;
    setErro(null);
    try {
      await descartar.mutateAsync({ id: paraDescartar.id, versao: paraDescartar.versao, motivo });
      setParaDescartar(null);
      setMotivo("");
      onAviso("Avaliação concluída: o líder que cadastrou será avisado.");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível concluir.");
    }
  }

  async function registrarSoVisita(i: Indicacao, existente: VisitanteLista) {
    setErro(null);
    try {
      await soVisita.mutateAsync({ id: i.id, versao: i.versao, visitanteId: existente.id });
      setParaSoVisita(null);
      onAviso(`Visita de ${existente.nome} registrada no cadastro que já existia.`);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível registrar a visita.");
    }
  }

  return (
    <div>
      <div role="group" aria-label="Situação dos visitantes pendentes" className="mb-3 flex gap-2">
        {(
          [
            { chave: "pendentes", rotulo: `Aguardando (${pendentes.length})` },
            { chave: "avaliadas", rotulo: `Avaliados (${avaliadas.length})` },
          ] as const
        ).map((t) => (
          <button
            key={t.chave}
            type="button"
            aria-pressed={ver === t.chave}
            onClick={() => setVer(t.chave)}
            className={`min-h-9 rounded-full border px-3 text-[13px] font-semibold transition-colors ${
              ver === t.chave
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-text hover:bg-surface-2"
            }`}
          >
            {t.rotulo}
          </button>
        ))}
      </div>

      {erro && !paraDescartar && !paraSoVisita && (
        <p role="alert" className="mb-3 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erro}
        </p>
      )}

      {isLoading && <p className="py-8 text-center text-sm text-muted">Carregando…</p>}
      {isError && (
        <p role="alert" className="py-8 text-center text-sm text-danger">
          Não foi possível carregar.{" "}
          <button type="button" onClick={() => refetch()} className="font-semibold underline">
            Tentar de novo
          </button>
        </p>
      )}
      {!isLoading && !isError && lista.length === 0 && (
        <p className="py-8 text-center text-sm text-muted">
          {ver === "pendentes"
            ? "Nenhum visitante pendente esperando avaliação."
            : "Nenhum visitante pendente avaliado ainda."}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {lista.map((i) => {
          const existente = porNome.get(normalizarNome(i.nome));
          return (
            <CardInformado key={i.id} informado={i} visao="ministerio">
              {i.situacao === "pendente" && existente && (
                <p className="w-full rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
                  Já existe um cadastro de {existente.nome}. Se for a mesma pessoa, registre só a
                  visita.
                </p>
              )}
              {podeAvaliar && i.situacao === "pendente" && (
                <>
                  <button
                    type="button"
                    className={BOTAO}
                    onClick={() => navigate(`/visitantes/informados/${i.id}/avaliar`)}
                  >
                    <Check size={14} aria-hidden="true" /> Adicionar
                  </button>
                  {existente && (
                    <button
                      type="button"
                      className={BOTAO}
                      disabled={soVisita.isPending}
                      onClick={() => {
                        setErro(null);
                        setParaSoVisita({ i, existente });
                      }}
                    >
                      Só registrar a visita
                    </button>
                  )}
                  <button
                    type="button"
                    className={BOTAO}
                    onClick={() => {
                      setErro(null);
                      setMotivo("");
                      setParaDescartar(i);
                    }}
                  >
                    <X size={14} aria-hidden="true" /> Não adicionar
                  </button>
                </>
              )}
            </CardInformado>
          );
        })}
      </ul>

      <ModalConfirmacao
        aberto={!!paraSoVisita}
        titulo="Só registrar a visita?"
        mensagem={`A visita de ${paraSoVisita?.existente.nome ?? "a pessoa"} será registrada no cadastro que já existe, sem criar um cadastro novo. O visitante pendente sai de "Aguardando".`}
        textoBotaoConfirmar="Registrar visita"
        variante="padrao"
        carregando={soVisita.isPending}
        erro={erro}
        onConfirmar={() => {
          if (paraSoVisita) void registrarSoVisita(paraSoVisita.i, paraSoVisita.existente);
        }}
        onCancelar={() => {
          setParaSoVisita(null);
          setErro(null);
        }}
      />

      <ModalConfirmacao
        aberto={!!paraDescartar}
        titulo="Não adicionar este visitante?"
        mensagem={`${paraDescartar?.nome ?? "A pessoa"} não será cadastrada e o líder que cadastrou será avisado. Se quiser, diga o motivo.`}
        textoBotaoConfirmar="Não adicionar"
        carregando={descartar.isPending}
        erro={erro}
        onConfirmar={confirmarDescarte}
        onCancelar={() => {
          setParaDescartar(null);
          setErro(null);
        }}
      >
        <CampoTexto
          label="Motivo (opcional)"
          name="motivoDescarte"
          rows={3}
          maxLength={300}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </ModalConfirmacao>
    </div>
  );
}
